/* Shop by Part catalog — progressive enhancement, no dependencies.
   Without JS every control still works as a normal GET form / link. */
(() => {
  if (window.ShopByPartLoaded) return;
  window.ShopByPartLoaded = true;

  const PRICE_DEBOUNCE = 650;

  class ShopByPart {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.catalogUrl = root.dataset.catalogUrl;
      this.controller = null;
      this.priceTimer = null;
      this.bind();
      history.replaceState({ sbp: true }, '', location.href);
    }

    get form() { return this.root.querySelector('[data-sbp-filters]'); }
    get drawer() { return this.root.querySelector('[data-sbp-drawer]'); }

    bind() {
      this.root.addEventListener('change', (e) => this.onChange(e));
      this.root.addEventListener('input', (e) => this.onInput(e));
      this.root.addEventListener('submit', (e) => this.onSubmit(e));
      this.root.addEventListener('click', (e) => this.onClick(e));
      window.addEventListener('popstate', (e) => { if (e.state && e.state.sbp) this.load(location.href, false); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this.closeDrawer(); });
    }

    /* ---------- events ---------- */
    onChange(e) {
      const el = e.target;
      if (el.hasAttribute('data-sbp-ignore')) return;
      if (el.hasAttribute('data-sbp-sort-radio')) {
        const select = this.root.querySelector('[data-sbp-sort]');
        if (select) select.value = el.value;
        return this.apply();
      }
      if (el.hasAttribute('data-sbp-price-input')) {
        clearTimeout(this.priceTimer);
        return this.apply();
      }
      if (el.form === this.form || el.hasAttribute('data-sbp-sort')) this.apply();
    }

    onInput(e) {
      const el = e.target;
      if (el.hasAttribute('data-sbp-list-search')) return this.filterList(el);
      if (el.hasAttribute('data-sbp-price-input')) {
        clearTimeout(this.priceTimer);
        this.priceTimer = setTimeout(() => this.apply(), PRICE_DEBOUNCE);
      }
    }

    onSubmit(e) {
      const form = e.target;
      if (form === this.form) { e.preventDefault(); return this.apply(); }
      if (form.hasAttribute('data-sbp-search')) {
        const q = form.querySelector('input[name="q"]').value.trim();
        if (!q) { e.preventDefault(); this.goToCatalog(); }
      }
    }

    onClick(e) {
      const t = e.target.closest('a, button');
      if (!t || !this.root.contains(t)) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

      if (t.hasAttribute('data-sbp-open')) return this.openDrawer();
      if (t.hasAttribute('data-sbp-close')) return this.closeDrawer();
      if (t.hasAttribute('data-sbp-show-more')) return this.toggleMore(t);
      if (t.hasAttribute('data-sbp-load-more')) { e.preventDefault(); return this.loadMore(t); }
      if (t.hasAttribute('data-sbp-clear-search')) { e.preventDefault(); return this.goToCatalog(); }
      if (t.hasAttribute('data-sbp-link')) { e.preventDefault(); return this.load(t.href); }
    }

    /* ---------- actions ---------- */
    buildUrl() {
      const form = this.form;
      const params = new URLSearchParams();
      for (const [k, v] of new FormData(form)) { if (String(v).trim() !== '') params.append(k, v); }
      const action = new URL(form.getAttribute('action'), location.origin);
      const qs = params.toString();
      return action.pathname + (qs ? `?${qs}` : '');
    }

    apply() { this.load(this.buildUrl()); }

    goToCatalog() {
      // Keep filters and sort, drop the search term.
      const params = new URLSearchParams(location.search);
      ['q', 'type', 'options[prefix]', 'page'].forEach((k) => params.delete(k));
      if (params.get('sort_by') === 'relevance') params.delete('sort_by');
      const qs = params.toString();
      location.href = this.catalogUrl + (qs ? `?${qs}` : '');
    }

    sectionUrl(url) {
      const u = new URL(url, location.origin);
      u.searchParams.set('section_id', this.sectionId);
      return u.toString();
    }

    async fetchSection(url) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const res = await fetch(this.sectionUrl(url), { signal: this.controller.signal, credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sbp]');
    }

    async load(url, push = true) {
      this.root.classList.add('is-loading');
      this.root.setAttribute('aria-busy', 'true');
      try {
        const next = await this.fetchSection(url);
        if (!next) throw new Error('Section not found');
        this.swap(next);
        if (push) history.pushState({ sbp: true }, '', url);
      } catch (err) {
        if (err.name === 'AbortError') return;
        window.location.href = url; // fall back to a normal page load
        return;
      } finally {
        this.root.classList.remove('is-loading');
        this.root.removeAttribute('aria-busy');
      }
    }

    swap(next) {
      // remember UI state
      const open = {};
      this.root.querySelectorAll('[data-sbp-group]').forEach((d) => { open[d.dataset.sbpGroup] = d.open; });
      const searches = {};
      this.root.querySelectorAll('[data-sbp-list-search]').forEach((i, idx) => {
        const g = i.closest('[data-sbp-group]'); if (g && i.value) searches[g.dataset.sbpGroup] = i.value;
      });
      const expanded = new Set([...this.root.querySelectorAll('[data-sbp-show-more][aria-expanded="true"]')].map((b) => b.closest('[data-sbp-group]').dataset.sbpGroup));
      const active = document.activeElement;
      const activeKey = active && this.root.contains(active) ? (active.name ? `${active.name}=${active.value}` : active.id) : null;

      const replace = (sel) => {
        const a = this.root.querySelector(sel); const b = next.querySelector(sel);
        if (a && b) a.innerHTML = b.innerHTML;
      };
      replace('[data-sbp-filter-body]');
      replace('[data-sbp-results]');
      replace('[data-sbp-search]');

      // The part-type nav sits outside the swapped regions, so sync it directly.
      const navA = this.root.querySelector('[data-sbp-nav]');
      const navB = next.querySelector('[data-sbp-nav]');
      if (navA && navB) navA.innerHTML = navB.innerHTML;
      else if (navA && !navB) navA.remove();
      else if (!navA && navB) {
        const hero = this.root.querySelector('.sbp-hero');
        if (hero) hero.insertAdjacentElement('afterend', document.importNode(navB, true));
      }

      const footA = this.root.querySelector('.sbp-filters__foot'); const footB = next.querySelector('.sbp-filters__foot');
      if (footA && footB) footA.innerHTML = footB.innerHTML;

      // restore UI state
      this.root.querySelectorAll('[data-sbp-group]').forEach((d) => {
        const k = d.dataset.sbpGroup;
        if (k in open) d.open = open[k] || d.open;
        if (searches[k]) { const i = d.querySelector('[data-sbp-list-search]'); if (i) { i.value = searches[k]; this.filterList(i); } }
        if (expanded.has(k)) { const b = d.querySelector('[data-sbp-show-more]'); if (b) this.toggleMore(b, true); }
      });
      if (activeKey) {
        const el = [...this.root.querySelectorAll('input, select, button')].find((x) => (x.name ? `${x.name}=${x.value}` : x.id) === activeKey);
        if (el) el.focus({ preventScroll: true });
      }
    }

    async loadMore(btn) {
      const wrap = btn.closest('[data-sbp-more]');
      wrap.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      try {
        const next = await this.fetchSection(btn.href);
        const grid = this.root.querySelector('[data-sbp-grid]');
        const items = next.querySelectorAll('[data-sbp-grid] > li');
        const firstNew = items[0];
        items.forEach((li) => grid.appendChild(document.importNode(li, true)));
        const nextMore = next.querySelector('[data-sbp-more]');
        if (nextMore) wrap.innerHTML = nextMore.innerHTML;
        const shown = wrap.querySelector('[data-sbp-shown]');
        if (shown) shown.textContent = grid.children.length;
        // move focus to the first newly loaded product for keyboard users
        const link = firstNew && grid.children[grid.children.length - items.length]?.querySelector('a[href]');
        if (link) link.focus({ preventScroll: true });
      } catch (err) {
        if (err.name !== 'AbortError') window.location.href = btn.href;
      } finally {
        wrap.classList.remove('is-loading');
      }
    }

    filterList(input) {
      const term = input.value.trim().toLowerCase();
      const group = input.closest('[data-sbp-group]');
      const list = group.querySelector('[data-sbp-list]');
      list.classList.toggle('sbp-list--searching', term.length > 0);
      list.querySelectorAll('[data-sbp-label]').forEach((li) => { li.hidden = term && !li.dataset.sbpLabel.includes(term); });
      const more = group.querySelector('[data-sbp-show-more]');
      if (more) more.hidden = term.length > 0;
    }

    toggleMore(btn, forceOpen) {
      const list = btn.closest('[data-sbp-group]').querySelector('[data-sbp-list]');
      const expand = forceOpen === true ? true : btn.getAttribute('aria-expanded') !== 'true';
      list.classList.toggle('sbp-list--collapsed', !expand);
      btn.setAttribute('aria-expanded', String(expand));
      if (!btn.dataset.label) btn.dataset.label = btn.textContent;
      btn.textContent = expand ? 'Show less' : btn.dataset.label;
    }

    openDrawer() {
      const d = this.drawer; const overlay = this.root.querySelector('.sbp-overlay');
      this.lastFocus = document.activeElement;
      d.classList.add('is-open'); overlay.hidden = false;
      document.documentElement.classList.add('sbp-lock');
      this.root.querySelectorAll('[data-sbp-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      const close = d.querySelector('[data-sbp-close]'); if (close) close.focus();
    }

    closeDrawer() {
      const d = this.drawer; const overlay = this.root.querySelector('.sbp-overlay');
      d.classList.remove('is-open'); overlay.hidden = true;
      document.documentElement.classList.remove('sbp-lock');
      this.root.querySelectorAll('[data-sbp-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      const opener = this.root.querySelector('[data-sbp-open]');
      if (opener && window.matchMedia('(max-width: 989px)').matches) opener.focus({ preventScroll: true });
    }
  }

  const init = () => {
    document.querySelectorAll('[data-sbp]').forEach((root) => {
      if (root.sbp) return;
      root.sbp = new ShopByPart(root);
      root.addEventListener('click', (e) => { if (e.target.classList.contains('sbp-overlay')) root.sbp.closeDrawer(); });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
  window.matchMedia('(min-width: 990px)').addEventListener('change', (m) => {
    if (m.matches) document.querySelectorAll('[data-sbp]').forEach((r) => r.sbp && r.sbp.closeDrawer());
  });
})();